import { useRef, useEffect, useState } from "react";
import { Check, Sparkles, ArrowRight, Zap, Crown, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const plans = [
  {
    id: "free",
    name: "Free",
    price: "0",
    currency: "FCFA",
    credits: "10 crédits/mois",
    popular: false,
    icon: Zap,
    gradient: "from-slate-500 to-slate-600",
    features: ["10 images HD/mois", "Tous les styles", "Export PNG/JPG"]
  },
  {
    id: "starter",
    name: "Starter",
    price: "5 900",
    currency: "FCFA",
    credits: "50 crédits/mois",
    popular: false,
    icon: Star,
    gradient: "from-blue-500 to-cyan-500",
    features: ["50 crédits/mois", "Floop Pro inclus", "Ad Studio inclus"]
  },
  {
    id: "pro",
    name: "Pro",
    price: "14 900",
    currency: "FCFA",
    credits: "250 crédits/mois",
    popular: true,
    icon: Crown,
    gradient: "from-violet-500 to-purple-500",
    features: ["250 crédits/mois", "Ad Studio illimité", "Accès API"]
  },
  {
    id: "studio",
    name: "Studio Max",
    price: "47 900",
    currency: "FCFA",
    credits: "500 crédits/mois",
    popular: false,
    icon: Sparkles,
    gradient: "from-amber-500 to-orange-500",
    features: ["500 crédits/mois", "Multi-utilisateurs", "Account manager"]
  },
];

export const PricingSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSelectPlan = (planId: string) => {
    if (planId === "free") {
      navigate("/auth");
    } else {
      navigate("/pricing");
    }
  };

  return (
    <section ref={sectionRef} className="py-16 md:py-28 bg-background relative overflow-hidden" id="pricing">
      {/* Background decorations */}
      <div className="absolute top-1/4 left-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary/5 blur-3xl rounded-full" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-accent/5 blur-3xl rounded-full" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-10 md:mb-16">
          <span 
            className={`inline-flex items-center gap-2 text-xs md:text-sm font-medium text-primary mb-3 md:mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
            Tarification
          </span>
          <h2 
            className={`text-2xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6 font-display transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Investissez dans{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">votre créativité</span>
          </h2>
          <p 
            className={`text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Des tarifs transparents. Commencez gratuitement.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 max-w-6xl mx-auto mb-8 md:mb-12">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                  <span className="bg-gradient-floop text-white text-xs font-bold px-2 md:px-4 py-1 md:py-1.5 rounded-full flex items-center gap-1 shadow-lg shadow-primary/30 whitespace-nowrap">
                    <Sparkles className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
                    <span className="hidden sm:inline">Populaire</span>
                    <span className="sm:hidden">★</span>
                  </span>
                </div>
              )}
              
              {/* Glow effect */}
              <div 
                className={`absolute -inset-0.5 bg-gradient-to-r ${plan.gradient} rounded-2xl md:rounded-3xl blur opacity-0 transition-opacity duration-500 ${
                  hoveredPlan === plan.id || plan.popular ? "opacity-30" : ""
                }`}
              />
              
              {/* Card */}
              <button
                onClick={() => handleSelectPlan(plan.id)}
                className={cn(
                  "relative w-full h-full bg-card/80 backdrop-blur-xl border rounded-2xl md:rounded-3xl p-3 md:p-6 text-left transition-all duration-500 overflow-hidden",
                  plan.popular
                    ? "border-primary/50"
                    : "border-border/50 hover:border-primary/30"
                )}
              >
                {/* Background shimmer */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, transparent 40%, hsl(var(--primary) / 0.05) 50%, transparent 60%)`,
                    backgroundSize: "200% 200%",
                    animation: hoveredPlan === plan.id ? "shine 1.5s ease infinite" : "none"
                  }}
                />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-r ${plan.gradient} flex items-center justify-center mb-3 md:mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <plan.icon className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  
                  {/* Plan name */}
                  <h3 className="text-base md:text-xl font-bold text-foreground mb-1 md:mb-2">{plan.name}</h3>
                  
                  {/* Price */}
                  <div className="mb-2 md:mb-4">
                    <span className={`text-xl md:text-3xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                      {plan.price}
                    </span>
                    <span className="text-xs md:text-sm text-muted-foreground ml-0.5 md:ml-1">{plan.currency}/mois</span>
                  </div>
                  
                  {/* Credits badge */}
                  <div className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2 md:px-3 py-1 rounded-full mb-3 md:mb-5">
                    <Zap className="w-3 h-3 md:w-4 md:h-4" />
                    <span className="hidden sm:inline">{plan.credits}</span>
                    <span className="sm:hidden">{plan.credits.replace('/mois', '')}</span>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-1.5 md:space-y-3 mb-4 md:mb-6 hidden sm:block">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
                        <Check className="w-3 h-3 md:w-4 md:h-4 text-success flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA */}
                  <div className={cn(
                    "w-full py-2 md:py-3 rounded-lg md:rounded-xl font-medium text-center transition-all duration-300 text-xs md:text-base",
                    plan.popular
                      ? "bg-gradient-floop text-white shadow-lg shadow-primary/30"
                      : "bg-secondary text-foreground group-hover:bg-primary group-hover:text-white"
                  )}>
                    {plan.id === "free" ? "Commencer" : "Choisir"}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>

        <div 
          className={`text-center transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ transitionDelay: "700ms" }}
        >
          <Link to="/pricing">
            <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-6 py-5 md:px-8 md:py-6 text-sm md:text-lg font-semibold group shadow-lg shadow-primary/30">
              Voir tous les détails
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Trust badges */}
        <div 
          className={`flex flex-wrap justify-center gap-3 md:gap-8 mt-8 md:mt-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ transitionDelay: "800ms" }}
        >
          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground bg-card/80 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-border/50">
            <Check className="w-4 h-4 md:w-5 md:h-5 text-success" />
            <span>Sans engagement</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground bg-card/80 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-border/50">
            <Check className="w-4 h-4 md:w-5 md:h-5 text-success" />
            <span>Pas de carte</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground bg-card/80 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-border/50">
            <Check className="w-4 h-4 md:w-5 md:h-5 text-success" />
            <span>Support 7j/7</span>
          </div>
        </div>
      </div>
    </section>
  );
};
